import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, isAdmin } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/format";
import { Megaphone, Plus, Loader2, Pencil, Trash2, Clock } from "lucide-react";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso); const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function Communications() {
  const { user } = useAuth();
  const isMgr = isAdmin(user);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", message: "", expires_at: "" });

  const load = async () => setItems((await api.get("/broadcasts")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ title: "", message: "", expires_at: "" }); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ title: b.title, message: b.message, expires_at: toLocalInput(b.expires_at) }); setOpen(true); };

  const save = async () => {
    setBusy(true);
    const payload = { title: form.title, message: form.message, expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null };
    try {
      if (editing) { await api.patch(`/broadcasts/${editing.id}`, payload); toast.success("Announcement updated."); }
      else { await api.post("/broadcasts", payload); toast.success("Announcement broadcast to your team."); }
      setOpen(false); setEditing(null); setForm({ title: "", message: "", expires_at: "" }); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (b) => { try { await api.delete(`/broadcasts/${b.id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } };

  const now = Date.now();
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-emerald-400" />
          <h1 className="font-display text-2xl font-bold">Announcements</h1>
        </div>
        {isMgr && <Button onClick={openNew} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="new-broadcast-btn"><Plus className="w-4 h-4 mr-1" /> Broadcast</Button>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121816] border-emerald-500/20">
          <DialogHeader><DialogTitle>{editing ? "Edit announcement" : "Broadcast a message"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="broadcast-title-input" /></div>
            <div><Label>Message</Label><Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="broadcast-message-input" /></div>
            <div><Label>Expiry date (optional)</Label><Input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="broadcast-expiry-input" /><p className="text-xs text-muted-foreground mt-1">After this time it is hidden from employees.</p></div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={busy || !form.title || !form.message} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="broadcast-send-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save changes" : "Send to everyone"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {items.length === 0 ? (
        <div className="card-emerald p-12 text-center text-muted-foreground">No announcements yet.</div>
      ) : (
        <div className="space-y-4" data-testid="broadcast-message-banner">
          {items.map((b) => {
            const expired = b.expires_at && new Date(b.expires_at).getTime() < now;
            return (
              <div key={b.id} className={`card-emerald p-6 border-l-4 ${expired ? "border-l-white/20 opacity-70" : "border-l-emerald-500"}`} data-testid="broadcast-item">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-lg font-semibold">{b.title}</h3>
                  {isMgr && (
                    <div className="flex gap-1 shrink-0">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(b)} data-testid={`edit-broadcast-${b.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" onClick={() => del(b)} data-testid={`del-broadcast-${b.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{b.message}</p>
                <div className="flex items-center gap-2 flex-wrap mt-3">
                  <p className="text-xs text-muted-foreground">{b.author_name} · {fmtDateTime(b.created_at)}</p>
                  {b.expires_at && <Badge className={`${expired ? "bg-white/10 text-muted-foreground" : "bg-amber-500/20 text-amber-300"} text-[10px] flex items-center gap-1`}><Clock className="w-3 h-3" />{expired ? "Expired" : "Expires"} {fmtDateTime(b.expires_at)}</Badge>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
